import React, { useState, useMemo, useEffect } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import { useLanguage } from "@/contexts/LanguageContext";
import { CategoryType } from "@/types";
import { Project } from "@/types";
import { useCategoryData } from "@/hooks/useCategoryData";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

interface CategoryChartProps {
  projects: Project[];
  projectToBatchIds: Record<string, string[]>;
  batchTitles: Record<string, string>;
  selectedCategory?: CategoryType;
  selectedProjectId?: string;
}

const CategoryChart: React.FC<CategoryChartProps> = ({
  projects,
  projectToBatchIds,
  batchTitles,
  selectedCategory,
  selectedProjectId,
}) => {
  const { language, t } = useLanguage();
  const [hoveredCategory, setHoveredCategory] = useState<string | null>(null);
  const [selectedCategoryForDetails, setSelectedCategoryForDetails] = useState<
    string | null
  >(null);
  const [clickedCategory, setClickedCategory] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [openAccordionItem, setOpenAccordionItem] = useState<string | null>(
    null
  );

  // Get category data using the hook
  const { categoryData, projectsMeasurements } = useCategoryData({
    projects,
    projectToBatchIds,
    batchTitles,
    selectedCategory,
    selectedProjectId,
  });

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const handleMouseEnter = (_: any, index: number) => {
    setHoveredCategory(chartData[index]?.category || null);
  };

  const handleMouseLeave = () => {
    setHoveredCategory(null);
  };

  const handleLegendHover = (category: string) => {
    setHoveredCategory(category);
  };

  const handleLegendLeave = () => {
    setHoveredCategory(null);
  };

  const handlePieClick = (entry: any) => {
    setClickedCategory(entry.category);
    // In mobile view, only open the accordion, not the detailed view
    if (isMobile) {
      setOpenAccordionItem(entry.category);
      setSelectedCategoryForDetails(null);
    } else {
      setSelectedCategoryForDetails(entry.category);
    }
  };

  const handleCategoryClick = (category: string) => {
    setClickedCategory(category);
    setSelectedCategoryForDetails(category);
  };

  const handleAccordionValueChange = (value: string | null) => {
    setOpenAccordionItem(value);
    // Update chart selection based on accordion state
    if (isMobile) {
      setClickedCategory(value);
    }
  };

  const totalMeasures = useMemo(() => {
    return categoryData.reduce((acc, item) => acc + item.totalMeasuresCount, 0);
  }, [categoryData]);

  const chartData = useMemo(() => {
    return categoryData.map((item) => ({
      category: item.category,
      displayName: item.displayName,
      value: item.totalMeasuresCount,
      color: item.color,
      description: item.description,
    }));
  }, [categoryData]);

  // Get the selected category details for the right panel
  const selectedCategoryData = useMemo(() => {
    if (!selectedCategoryForDetails) return null;
    return categoryData.find(
      (item) => item.category === selectedCategoryForDetails
    );
  }, [selectedCategoryForDetails, categoryData]);

  // Calculate projects involved for selected category
  const projectsInvolved = useMemo(() => {
    if (!selectedCategoryForDetails) return 0;

    const filteredProjects = selectedProjectId
      ? projects.filter((project) => project.id === selectedProjectId)
      : projects;

    let projectCount = 0;
    filteredProjects.forEach((project) => {
      const measurements = projectsMeasurements[project.id];
      if (!measurements) return;

      const { planned, documented } = measurements;
      let hasCategoryMeasures = false;

      // Check documented measures
      if (documented.length > 0) {
        hasCategoryMeasures = documented.some(
          (item) => item.category === selectedCategoryForDetails
        );
      }
      // Check planned measures (check regardless of documented measures)
      if (!hasCategoryMeasures && planned.length > 0) {
        hasCategoryMeasures = planned.some(
          (measure) => measure.category === selectedCategoryForDetails
        );
      }

      if (hasCategoryMeasures) {
        projectCount++;
      }
    });

    return projectCount;
  }, [
    selectedCategoryForDetails,
    projects,
    projectsMeasurements,
    selectedProjectId,
  ]);

  if (chartData.length === 0) {
    return (
      <div className="h-full flex items-center justify-center">
        <p className="text-sm text-muted-foreground">{t("common.noData")}</p>
      </div>
    );
  }

  // Split categories into two columns for the legend
  const leftColumn = chartData.slice(0, Math.ceil(chartData.length / 2));
  const rightColumn = chartData.slice(Math.ceil(chartData.length / 2));

  return (
    <div className="bg-white rounded-xl border p-10 sm:p-10 flex flex-col lg:flex-row">
      {/* Donut Chart */}
      <div className="flex-[0.7] lg:pr-4 -mt-4 md:mt-0">
        {" "}
        {/* Reduced from flex-1 to flex-[0.4] */}
        <ResponsiveContainer width="100%" height={isMobile ? 320 : 450}>
          <PieChart>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              innerRadius={isMobile ? 80 : 100}
              outerRadius={isMobile ? 120 : 150}
              paddingAngle={2}
              dataKey="value"
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
              onClick={handlePieClick}
            >
              {chartData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={
                    (
                      isMobile
                        ? clickedCategory
                        : hoveredCategory || clickedCategory
                    )
                      ? (
                          isMobile
                            ? clickedCategory === entry.category
                            : hoveredCategory === entry.category ||
                              clickedCategory === entry.category
                        )
                        ? entry.color
                        : `${entry.color}20`
                      : entry.color
                  }
                />
              ))}
            </Pie>
            {/* Center text showing percentage when category is hovered or clicked */}
            {(isMobile
              ? clickedCategory
              : hoveredCategory || clickedCategory) && (
              <text
                x="50%"
                y="50%"
                textAnchor="middle"
                dominantBaseline="middle"
                className="text-2xl font-bold"
                style={{
                  fill:
                    categoryData.find(
                      (item) =>
                        item.category ===
                        (isMobile
                          ? clickedCategory
                          : hoveredCategory || clickedCategory)
                    )?.color || "#000",
                }}
              >
                {(
                  ((chartData.find(
                    (item) =>
                      item.category ===
                      (isMobile
                        ? clickedCategory
                        : hoveredCategory || clickedCategory)
                  )?.value || 0) /
                    totalMeasures) *
                  100
                ).toFixed(1)}
                %
              </text>
            )}
          </PieChart>
        </ResponsiveContainer>
        {/* Instruction text underneath the chart */}
        <div className="text-center mt-[-12px]">
          <p className="text-sm text-gray-500">
            {t("categoryOverview.clickToExplore")}
          </p>
        </div>
      </div>

      {/* Vertical Divider */}
      <div className="hidden lg:block w-px bg-gray-200 mx-4" />

      {/* Right Panel */}
      <div className="flex-1 lg:pl-12 lg:pr-12 lg:py-6 mt-6 lg:mt-0">
        {" "}
        {/* Increased padding from lg:pl-8 to lg:pl-12, lg:pr-8 to lg:pr-12, added lg:py-6 */}
        {selectedCategoryData ? (
          // Selected category details view
          <div className="lg:pt-12">
            <div className="flex items-center gap-3 mb-4">
              <button
                onClick={() => {
                  setSelectedCategoryForDetails(null);
                  setClickedCategory(null);
                  setHoveredCategory(null);
                }}
                className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
              >
                ← {t("common.back")}
              </button>
            </div>
            <div className="mb-4">
              <div
                className="h-2 w-8 rounded-[9px] mb-2"
                style={{
                  backgroundColor: selectedCategoryData.color,
                }}
              />
              <h3 className="text-lg font-semibold text-gray-800 mb-2">
                {selectedCategoryData.displayName}
              </h3>
            </div>
            <p className="text-sm text-gray-600 mb-6">
              {selectedCategoryData.description}
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div
                className="px-4 py-8 rounded-lg"
                style={{ backgroundColor: `${selectedCategoryData.color}20` }}
              >
                <p className="text-sm font-medium text-gray-700 mb-1">
                  {selectedCategoryData.totalMeasuresCount}
                </p>
                <p className="text-xs text-gray-600">
                  {t("categoryOverview.measures")}
                </p>
              </div>
              <div
                className="px-4 py-8 rounded-lg"
                style={{ backgroundColor: `${selectedCategoryData.color}20` }}
              >
                <p className="text-sm font-medium text-gray-700 mb-1">
                  {projectsInvolved}
                </p>
                <p className="text-xs text-gray-600">
                  {t("categoryOverview.projectsInvolved")}
                </p>
              </div>
            </div>
          </div>
        ) : (
          // Default legend view
          <>
            <div className="lg:pt-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-2 leading-tight">
                {t("categoryOverview.distributionTitle")}
              </h3>
              <p className="text-sm text-gray-600 mb-6">
                {t("categoryOverview.distributionDescription")}
              </p>

              {isMobile ? (
                // Mobile accordion view
                <Accordion
                  type="single"
                  collapsible
                  className="w-full"
                  value={openAccordionItem}
                  onValueChange={handleAccordionValueChange}
                >
                  {chartData.map((item, index) => {
                    // Calculate projects involved for this category
                    const categoryProjectsInvolved = (() => {
                      const filteredProjects = selectedProjectId
                        ? projects.filter(
                            (project) => project.id === selectedProjectId
                          )
                        : projects;

                      let projectCount = 0;
                      filteredProjects.forEach((project) => {
                        const measurements = projectsMeasurements[project.id];
                        if (!measurements) return;

                        const { planned, documented } = measurements;
                        let hasCategoryMeasures = false;

                        // Check documented measures
                        if (documented.length > 0) {
                          hasCategoryMeasures = documented.some(
                            (measure) => measure.category === item.category
                          );
                        }
                        if (!hasCategoryMeasures && planned.length > 0) {
                          hasCategoryMeasures = planned.some(
                            (measure) => measure.category === item.category
                          );
                        }

                        if (hasCategoryMeasures) {
                          projectCount++;
                        }
                      });

                      return projectCount;
                    })();

                    return (
                      <AccordionItem
                        key={item.category}
                        value={item.category}
                        className={`border-b ${
                          index < chartData.length - 1 ? "" : "border-none"
                        }`}
                      >
                        <AccordionTrigger className="flex items-center gap-3 px-4 py-4 transition-all duration-200">
                          <div className="flex items-center gap-3 flex-1">
                            <div
                              className="h-2 w-8 rounded-[9px]"
                              style={{ backgroundColor: item.color }}
                            />
                            <span className="text-sm font-semibold text-gray-900">
                              {item.displayName}
                            </span>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="px-2 pb-3">
                          <div className="mt-3">
                            <p className="text-sm text-gray-600 mb-4">
                              {item.description}
                            </p>
                            <div className="grid grid-cols-2 gap-3">
                              <div
                                className="px-3 py-4 rounded-lg"
                                style={{ backgroundColor: `${item.color}20` }}
                              >
                                <p className="text-sm font-semibold text-gray-700 mb-1">
                                  {item.value}
                                </p>
                                <p className="text-xs text-gray-600">
                                  {t("categoryOverview.measures")}
                                </p>
                              </div>
                              <div
                                className="px-3 py-4 rounded-lg"
                                style={{ backgroundColor: `${item.color}20` }}
                              >
                                <p className="text-sm font-semibold text-gray-700 mb-1">
                                  {categoryProjectsInvolved}
                                </p>
                                <p className="text-xs text-gray-600">
                                  {t("categoryOverview.projectsInvolved")}
                                </p>
                              </div>
                            </div>
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    );
                  })}
                </Accordion>
              ) : (
                // Desktop grid view
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Left Column */}
                  <div className="space-y-3">
                    {leftColumn.map((item) => (
                      <div
                        key={item.category}
                        className={`flex items-center gap-3 px-4 py-2 rounded-lg cursor-pointer transition-all duration-200 border border-gray-200 bg-white ${
                          hoveredCategory === item.category ||
                          clickedCategory === item.category
                            ? "shadow-md"
                            : ""
                        }`}
                        onMouseEnter={() => handleLegendHover(item.category)}
                        onMouseLeave={handleLegendLeave}
                        onClick={() => handleCategoryClick(item.category)}
                      >
                        <div
                          className={`h-2 w-8 rounded-[9px] transition-all duration-200 ${
                            hoveredCategory === item.category ||
                            clickedCategory === item.category
                              ? "scale-110"
                              : ""
                          }`}
                          style={{
                            backgroundColor: item.color,
                          }}
                        />
                        <span className="text-sm font-medium text-gray-700 flex-1">
                          {item.displayName}
                        </span>
                        <span className="text-sm font-semibold text-gray-900">
                          {new Intl.NumberFormat(
                            language === "de" ? "de-DE" : "en-US"
                          ).format(item.value)}
                        </span>
                      </div>
                    ))}
                  </div>

                  {/* Right Column */}
                  <div className="space-y-3">
                    {rightColumn.map((item) => (
                      <div
                        key={item.category}
                        className={`flex items-center gap-3 px-4 py-2 rounded-lg cursor-pointer transition-all duration-200 border border-gray-200 bg-white ${
                          hoveredCategory === item.category ||
                          clickedCategory === item.category
                            ? "shadow-md"
                            : ""
                        }`}
                        onMouseEnter={() => handleLegendHover(item.category)}
                        onMouseLeave={handleLegendLeave}
                        onClick={() => handleCategoryClick(item.category)}
                      >
                        <div
                          className={`h-2 w-8 rounded-[9px] transition-all duration-200 ${
                            hoveredCategory === item.category ||
                            clickedCategory === item.category
                              ? "scale-110"
                              : ""
                          }`}
                          style={{
                            backgroundColor: item.color,
                          }}
                        />
                        <span className="text-sm font-medium text-gray-700 flex-1">
                          {item.displayName}
                        </span>
                        <span className="text-sm font-semibold text-gray-900">
                          {new Intl.NumberFormat(
                            language === "de" ? "de-DE" : "en-US"
                          ).format(item.value)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CategoryChart;
